import { createElement, UIEventHandler, useRef, CSSProperties, useEffect } from 'react';
import { Direction, useIsRTL } from '../../locale';
import { getRTLOffsetType } from './domHelpers';
import cx from 'classnames';
import cn from './VirtualScrollbar.css.json';
import './VirtualScrollbar.css';

export type VirtualScrollbarDirection = 'horizontal' | 'vertical';

export interface VirtualScrollbarProps {
  size: number;
  dir?: VirtualScrollbarDirection;
  value?: number;
  className?: string;
  style?: CSSProperties;
  onScroll?: UIEventHandler<HTMLDivElement>;
}

export const VirtualScrollbar = (props: VirtualScrollbarProps) => {
  const { dir, onScroll, size, className, ...otherProps } = props;
  const isRTL = useIsRTL();
  const isHorizontal = dir === 'horizontal';
  const styleProp = isHorizontal ? 'width' : 'height';
  const scrollProp = isHorizontal ? 'scrollLeft' : 'scrollTop';
  const ref = useRef<HTMLDivElement | null>(null);
  // consumers will sometimes want to specify where the bar has scrolled to
  const value = props.value ?? 0;

  useEffect(() => {
    if (!ref.current) return;
    const scrollPos = isHorizontal
      ? getActualScrollLeft({
          normalizedScrollLeft: value,
          direction: isRTL ? 'rtl' : 'ltr',
          scrollWidth: ref.current.scrollWidth,
          clientWidth: ref.current.clientWidth,
        })
      : value;
    if (ref.current[scrollProp] !== scrollPos) {
      ref.current[scrollProp] = scrollPos;
    }
  }, [value, scrollProp, isHorizontal, isRTL]);

  return (
    <div
      ref={ref}
      className={cx(
        cn.container,
        {
          [cn.hbar]: isHorizontal,
          [cn.vbar]: !isHorizontal,
        },
        className
      )}
      onScroll={props.onScroll}
      {...otherProps}
    >
      <div
        className={cn.body}
        style={{
          [styleProp]: size,
        }}
      />
    </div>
  );
};

export default VirtualScrollbar;

export const getActualScrollLeft = (args: {
  normalizedScrollLeft: number;
  direction: Direction;
  scrollWidth: number;
  clientWidth: number;
}): number => {
  const { normalizedScrollLeft, direction, scrollWidth, clientWidth } = args;
  if (direction === 'ltr') return Math.max(0, normalizedScrollLeft);

  switch (getRTLOffsetType()) {
    case 'negative':
      return -normalizedScrollLeft;
    case 'positive-descending':
      return scrollWidth - clientWidth - normalizedScrollLeft;
  }

  return normalizedScrollLeft;
};
